package com.islamicmedia.site.components

import androidx.compose.runtime.*
import com.islamicmedia.shared.domain.model.BulkBatchState
import com.islamicmedia.shared.domain.model.BulkItemStatus
import com.islamicmedia.shared.domain.model.BulkResultItem
import com.islamicmedia.shared.domain.model.BulkResultStatus
import com.islamicmedia.shared.domain.model.BulkScanItem
import com.islamicmedia.shared.domain.model.BulkVerdict
import com.islamicmedia.shared.domain.model.BulkScanPhase
import com.islamicmedia.shared.domain.model.BulkScanState
import com.islamicmedia.shared.presentation.MediaCheckerViewModel
import com.islamicmedia.site.theme.*
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.w3c.dom.HTMLInputElement
import org.w3c.files.FileReader

/**
 * Bulk spine scan (Index's ActiveView.BULK_SCAN), for library accounts: choose one photo of a stack
 * of books, wait while the spines are read, then check the list. Every phase is a screen inside the
 * card; the card's ✕ calls closeBulkScan(). The footer button starts the batch and the RESULTS phase follows it. Mirrors composeApp/.../screens/BulkScanScreen.kt — keep the two in step.
 */
@Composable
fun BulkScanScreen(viewModel: MediaCheckerViewModel, state: BulkScanState) {
    val entitlement by viewModel.entitlement.collectAsState()
    Div(attrs = {
        style {
            padding(32.px, 20.px)
            property("box-sizing", "border-box")
            property("max-width", "560px")
            property("margin", "0 auto")
            property("width", "100%")
        }
    }) {
        when (state.phase) {
            BulkScanPhase.PICK -> BulkPick(onPhoto = viewModel::onBulkScanPhoto, onClose = viewModel::closeBulkScan)
            BulkScanPhase.SCANNING -> BulkScanning(foundCount = state.foundCount, onCancel = viewModel::closeBulkScan)
            BulkScanPhase.ERROR -> BulkError(state.error, onRetry = viewModel::retakeBulkScan, onClose = viewModel::closeBulkScan)
            BulkScanPhase.REVIEW -> BulkReview(viewModel, state, packRemaining = entitlement?.takeIf { it.isLibrary }?.packRemaining)
            BulkScanPhase.RESULTS -> BulkResults(viewModel, state)
        }
    }
}

// ── Shared bits ─────────────────────────────────────────────────────────────────────

@Composable
private fun BulkTitle(text: String) {
    Div(attrs = {
        style {
            marginBottom(12.px)
            fontSize(18.px)
            fontWeight(700)
            color(Gray900)
            property("text-align", "center")
            property("font-family", TitleFontFamily)
        }
    }) { Text(text) }
}

@Composable
private fun BulkHelp(text: String) {
    Div(attrs = {
        style {
            marginBottom(20.px)
            fontSize(14.px)
            color(Gray600)
            property("line-height", "1.5")
            property("text-align", "center")
        }
    }) { Text(text) }
}

@Composable
private fun BulkButton(
    label: String,
    primary: Boolean = false,
    enabled: Boolean = true,
    onClick: () -> Unit
) {
    Button(attrs = {
        onClick { if (enabled) onClick() }
        if (!enabled) attr("disabled", "")
        style {
            padding(12.px, 22.px)
            borderRadius(24.px)
            fontSize(15.px)
            fontWeight(600)
            cursor(if (enabled) "pointer" else "default")
            property("font-family", "inherit")
            property("border", if (primary) "none" else "1.5px solid ${Primary}")
            property("background", if (primary) (if (enabled) "#0E7C7B" else "#D4D4D4") else "transparent")
            color(if (primary) Color.white else Primary)
            opacity(if (enabled) 1.0 else 0.8)
        }
    }) { Text(label) }
}

@Composable
private fun BulkTextLink(label: String, onClick: () -> Unit) {
    Button(attrs = {
        onClick { onClick() }
        style {
            property("background", "transparent")
            property("border", "none")
            padding(8.px, 10.px)
            fontSize(14.px)
            fontWeight(600)
            color(Primary)
            cursor("pointer")
            property("font-family", "inherit")
        }
    }) { Text(label) }
}

private fun readPickedFile(inputId: String, onBytes: (ByteArray) -> Unit) {
    val input = document.getElementById(inputId) as? HTMLInputElement
    val file = input?.files?.item(0)
    if (file != null) {
        val reader = FileReader()
        reader.onload = {
            val arrayBuffer = reader.result as? ArrayBuffer
            if (arrayBuffer != null) {
                val int8Array = Int8Array(arrayBuffer)
                onBytes(ByteArray(int8Array.length) { i -> int8Array.asDynamic()[i] as Byte })
            }
        }
        reader.readAsArrayBuffer(file)
    }
    input?.value = ""
}

// ── PICK / SCANNING / ERROR ─────────────────────────────────────────────────────────

@Composable
private fun BulkPick(onPhoto: (ByteArray) -> Unit, onClose: () -> Unit) {
    val fileInputId = remember { "bulk-file-picker-${kotlin.random.Random.nextInt()}" }
    // Same hidden image input the search bar's camera button uses.
    Input(type = InputType.File) {
        id(fileInputId)
        attr("accept", "image/*")
        style { display(DisplayStyle.None) }
        onChange { readPickedFile(fileInputId, onPhoto) }
    }
    BulkTitle("Scan a stack of books")
    BulkHelp("Line the books up with the spines facing the camera, in good light, and take one photo. Up to about 20 books at a time.")
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            property("gap", "10px")
        }
    }) {
        BulkButton("Choose a photo", primary = true) {
            (document.getElementById(fileInputId) as? HTMLInputElement)?.click()
        }
        BulkTextLink("Close", onClose)
    }
}

@Composable
private fun BulkScanning(foundCount: Int, onCancel: () -> Unit) {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            property("gap", "10px")
            padding(24.px, 0.px)
        }
    }) {
        SearchingAnimation()
        Div(attrs = {
            style { fontSize(16.px); fontWeight(600); color(Gray700) }
        }) {
            Text(if (foundCount > 0) "Found about $foundCount spines. Reading them now…" else "Reading the photo…")
        }
        BulkTextLink("Cancel", onCancel)
    }
}

@Composable
private fun BulkError(error: String?, onRetry: () -> Unit, onClose: () -> Unit) {
    BulkTitle("That didn't work")
    BulkHelp(error ?: "Something went wrong reading that photo.")
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            property("gap", "10px")
        }
    }) {
        BulkButton("Try again", primary = true, onClick = onRetry)
        BulkTextLink("Close", onClose)
    }
}

// ── REVIEW ──────────────────────────────────────────────────────────────────────────

private fun statusTag(status: BulkItemStatus): String? = when (status) {
    BulkItemStatus.MATCHED -> null
    BulkItemStatus.CHECK -> "Check this"
    BulkItemStatus.UNVERIFIED -> "Not in our catalog"
    BulkItemStatus.GUESS -> "Not sure"
    BulkItemStatus.UNREADABLE -> "Couldn't read"
    BulkItemStatus.MANUAL -> "You added"
}

/** Which dialog is open over the list: fix one row, or add a missed book. */
private sealed interface BulkDialog {
    data class Edit(val item: BulkScanItem) : BulkDialog
    object Add : BulkDialog
}

@Composable
private fun BulkReview(viewModel: MediaCheckerViewModel, state: BulkScanState, packRemaining: Int?) {
    var dialog by remember { mutableStateOf<BulkDialog?>(null) }
    val items = state.items

    InjectSpinKeyframes()
    val noun = if (items.size == 1) "book" else "books"
    BulkTitle(if (state.scanning) "${items.size} $noun so far" else "We found ${items.size} $noun")
    BulkHelp("Untick anything that isn't right. Click a title to fix it.")

    if (state.scanning) {
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                property("gap", "8px")
                marginBottom(12.px)
                padding(8.px, 12.px)
                borderRadius(8.px)
                backgroundColor(PrimaryPale)
                color(Primary)
                fontSize(13.px)
                fontWeight(600)
            }
        }) {
            Span(attrs = {
                style {
                    width(14.px); height(14.px)
                    property("flex-shrink", "0")
                    borderRadius(50.percent)
                    property("border", "2px solid #D4D4D4")
                    property("border-top-color", "#0E7C7B")
                    property("box-sizing", "border-box")
                    property("animation", "spin 0.8s linear infinite")
                }
            })
            Text(if (state.foundCount > 0) "Reading the spines… ${items.size} of about ${state.foundCount}" else "Reading the spines…")
        }
    }

    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            property("border-top", "1px solid ${"#E5E5E5"}")
        }
    }) {
        // key() so typing in one row's box survives edits to another row.
        items.forEach { item ->
            key(item.id) {
                BulkRow(
                    item = item,
                    onToggle = { viewModel.toggleBulkItem(item.id) },
                    onOpenEdit = { dialog = BulkDialog.Edit(item) },
                    onTyped = { viewModel.editBulkItem(item.id, it, item.author) },
                    onRemove = { viewModel.removeBulkItem(item.id) },
                )
            }
        }
    }

    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            property("flex-wrap", "wrap")
            justifyContent(JustifyContent.Center)
            property("gap", "4px 8px")
            marginTop(12.px)
        }
    }) {
        BulkTextLink("Add a book I missed") { dialog = BulkDialog.Add }
        BulkTextLink("Retake photo", viewModel::retakeBulkScan)
        if (items.isNotEmpty()) {
            val allOn = items.filter { it.canAnalyze }.all { it.selected }
            if (allOn) BulkTextLink("Select none") { viewModel.setAllBulkSelected(false) }
            else BulkTextLink("Select all") { viewModel.setAllBulkSelected(true) }
        }
    }

    // Footer: starts the batch.
    val n = state.selectedCount
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            property("gap", "6px")
            marginTop(20.px)
            paddingTop(16.px)
            property("border-top", "1px solid #E5E5E5")
        }
    }) {
        if (state.error != null) BulkErrorText(state.error!!)
        BulkButton(
            if (state.starting) "Starting…" else "Analyze $n ${if (n == 1) "book" else "books"}",
            primary = true,
            enabled = n > 0 && !state.starting
        ) { viewModel.startBulkAnalysis() }
        if (state.scanning) {
            Div(attrs = {
                style { fontSize(12.px); color(Gray500); property("text-align", "center") }
            }) { Text("Still reading. Books found after you start won't be included.") }
        }
        if (packRemaining != null) {
            Div(attrs = { style { fontSize(13.px); color(Gray600) } }) {
                Text("Uses $n of your $packRemaining searches")
            }
        }
    }

    when (val d = dialog) {
        is BulkDialog.Edit -> BulkEditDialog(
            heading = "Fix this book",
            initialTitle = d.item.title,
            initialAuthor = d.item.author,
            onSave = { t, a -> viewModel.editBulkItem(d.item.id, t, a); dialog = null },
            onDismiss = { dialog = null }
        )
        BulkDialog.Add -> BulkEditDialog(
            heading = "Add a book I missed",
            initialTitle = "",
            initialAuthor = "",
            onSave = { t, a -> viewModel.addBulkItem(t, a); dialog = null },
            onDismiss = { dialog = null }
        )
        null -> {}
    }
}

@Composable
private fun BulkRow(
    item: BulkScanItem,
    onToggle: () -> Unit,
    onOpenEdit: () -> Unit,
    onTyped: (String) -> Unit,
    onRemove: () -> Unit,
) {
    val unreadable = item.status == BulkItemStatus.UNREADABLE && item.title.isBlank()
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            property("gap", "10px")
            padding(10.px, 2.px)
            property("border-bottom", "1px solid #E5E5E5")
        }
    }) {
        CheckboxInput(checked = item.selected) {
            attr("aria-label", "Include this book")
            if (!item.canAnalyze) attr("disabled", "")
            onChange { onToggle() }
            style { width(20.px); height(20.px); property("flex-shrink", "0"); property("accent-color", "#0E7C7B"); cursor("pointer") }
        }

        // Picture: the cover, the spine photo for a spine we couldn't read, else a neutral box.
        val src = when {
            !item.coverUrl.isNullOrBlank() -> item.coverUrl
            !item.thumbBase64.isNullOrBlank() -> "data:image/jpeg;base64,${item.thumbBase64}"
            else -> null
        }
        val wide = src != null && item.coverUrl.isNullOrBlank()
        if (src != null) {
            Img(src = src, alt = "", attrs = {
                style {
                    width((if (wide) 56 else 40).px)
                    height(56.px)
                    property("object-fit", if (wide) "contain" else "cover")
                    borderRadius(6.px)
                    backgroundColor(Gray100)
                    property("flex-shrink", "0")
                }
            })
        } else {
            Div(attrs = {
                style {
                    width(40.px); height(56.px); borderRadius(6.px)
                    backgroundColor(Gray100)
                    property("border", "1px solid #E5E5E5")
                    property("box-sizing", "border-box")
                    property("flex-shrink", "0")
                }
            })
        }

        Div(attrs = { style { property("flex", "1"); property("min-width", "0") } }) {
            if (unreadable) {
                var typed by remember { mutableStateOf("") }
                fun commit() { if (typed.isNotBlank()) onTyped(typed) }
                TextInput(value = typed) {
                    attr("placeholder", "Type the title")
                    attr("aria-label", "Type the title")
                    onInput { typed = it.value }
                    // Saved on Enter or when you click away: saving on every key would turn the row
                    // into a normal one mid-word.
                    onKeyDown { if (it.key == "Enter") commit() }
                    addEventListener("blur") { commit() }
                    style {
                        width(100.percent)
                        padding(8.px, 10.px)
                        fontSize(15.px)
                        borderRadius(8.px)
                        property("border", "1.5px solid #D4D4D4")
                        property("box-sizing", "border-box")
                        property("font-family", "inherit")
                    }
                }
            } else {
                Div(attrs = {
                    onClick { onOpenEdit() }
                    style {
                        fontSize(15.px); fontWeight(600); color(Gray900)
                        cursor("pointer")
                        property("overflow-wrap", "anywhere")
                    }
                }) { Text(item.title) }
                val sub = listOf(item.author, item.year.orEmpty()).filter { it.isNotBlank() }.joinToString(" · ")
                if (sub.isNotEmpty()) {
                    Div(attrs = { style { fontSize(13.px); color(Gray600); marginTop(2.px) } }) { Text(sub) }
                }
            }
            val tag = statusTag(item.status)
            if (tag != null) {
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        property("flex-wrap", "wrap")
                        property("gap", "6px")
                        marginTop(4.px)
                    }
                }) {
                    if (tag != null) {
                        val warn = item.status != BulkItemStatus.MANUAL
                        Span(attrs = {
                            style {
                                padding(2.px, 8.px)
                                borderRadius(10.px)
                                fontSize(11.px)
                                fontWeight(600)
                                backgroundColor(if (warn) AccentLight else PrimaryPale)
                                color(if (warn) Accent else Primary)
                            }
                        }) { Text(tag) }
                    }
                }
            }
        }

        Button(attrs = {
            attr("aria-label", "Remove this book")
            onClick { onRemove() }
            style {
                width(32.px); height(32.px)
                property("flex-shrink", "0")
                property("background", "transparent")
                property("border", "none")
                borderRadius(50.percent)
                fontSize(14.px)
                color(Gray400)
                cursor("pointer")
            }
        }) { Text("✕") }
    }
}

// ── RESULTS ─────────────────────────────────────────────────────────────────────────

@Composable
private fun BulkErrorText(text: String) {
    Div(attrs = {
        style {
            fontSize(13.px)
            color(ScoreRed)
            property("text-align", "center")
            property("line-height", "1.4")
        }
    }) { Text(text) }
}

@Composable
private fun BulkResults(viewModel: MediaCheckerViewModel, state: BulkScanState) {
    InjectSpinKeyframes()
    val results = state.results
    val total = results.size
    val done = state.batchDone
    val finished = state.resultsFinished

    BulkTitle(if (done) "All done" else "Analyzing $total ${if (total == 1) "book" else "books"}")

    if (state.offline) {
        Div(attrs = {
            style {
                marginBottom(12.px)
                padding(8.px, 12.px)
                borderRadius(8.px)
                backgroundColor(AccentLight)
                color(Accent)
                fontSize(13.px)
                property("line-height", "1.4")
                property("text-align", "center")
            }
        }) { Text("Can't reach the server right now. Your books are still being analyzed, and we'll keep checking.") }
    }
    if (state.error != null) {
        Div(attrs = { style { marginBottom(12.px) } }) { BulkErrorText(state.error!!) }
    }

    // Thin progress bar + count.
    val pct = if (total == 0) 0 else (finished * 100 / total)
    Div(attrs = {
        style {
            height(6.px)
            borderRadius(3.px)
            backgroundColor(Gray200)
            property("overflow", "hidden")
        }
    }) {
        Div(attrs = {
            style {
                height(100.percent)
                width(pct.percent)
                backgroundColor(Primary)
                property("transition", "width 0.4s ease")
            }
        })
    }
    Div(attrs = {
        style { fontSize(13.px); color(Gray600); marginTop(6.px); property("text-align", "center") }
    }) { Text("$finished of $total finished") }

    if (!done) {
        Div(attrs = {
            style { fontSize(13.px); color(Gray500); marginTop(4.px); marginBottom(12.px); property("text-align", "center") }
        }) { Text("You can close this. Your books keep being analyzed.") }
    } else {
        val ok = results.filter { it.status == BulkResultStatus.DONE }
        val summary = listOf(
            BulkVerdict.MINOR to "Minor",
            BulkVerdict.MODERATE to "Moderate",
            BulkVerdict.MAJOR to "Major"
        ).mapNotNull { (v, label) ->
            val c = ok.count { it.verdict == v }
            if (c > 0) "$c $label" else null
        }.joinToString(" · ")
        Div(attrs = {
            style { fontSize(14.px); fontWeight(600); color(Gray700); marginTop(8.px); marginBottom(12.px); property("text-align", "center") }
        }) { Text(summary) }
    }

    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            property("border-top", "1px solid #E5E5E5")
        }
    }) {
        results.forEach { item -> key(item.id) { BulkResultRow(item) { viewModel.openBulkResult(item) } } }
    }

    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            property("gap", "10px")
            marginTop(20.px)
            paddingTop(16.px)
            property("border-top", "1px solid #E5E5E5")
        }
    }) {
        BulkButton("Scan another stack", primary = true, onClick = viewModel::retakeBulkScan)
        BulkTextLink(if (done) "Close" else "Close (keeps running)", viewModel::closeBulkScan)
    }
}

@Composable
private fun BulkResultRow(item: BulkResultItem, onOpen: () -> Unit) {
    val clickable = item.status == BulkResultStatus.DONE
    Div(attrs = {
        if (clickable) {
            onClick { onOpen() }
            attr("role", "button")
        }
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            property("gap", "10px")
            padding(10.px, 2.px)
            property("border-bottom", "1px solid #E5E5E5")
            if (clickable) cursor("pointer")
        }
    }) {
        if (!item.coverUrl.isNullOrBlank()) {
            Img(src = item.coverUrl!!, alt = "", attrs = {
                style {
                    width(40.px); height(56.px)
                    property("object-fit", "cover")
                    borderRadius(6.px)
                    backgroundColor(Gray100)
                    property("flex-shrink", "0")
                }
            })
        } else {
            Div(attrs = {
                style {
                    width(40.px); height(56.px); borderRadius(6.px)
                    backgroundColor(Gray100)
                    property("border", "1px solid #E5E5E5")
                    property("box-sizing", "border-box")
                    property("flex-shrink", "0")
                }
            })
        }

        Div(attrs = { style { property("flex", "1"); property("min-width", "0") } }) {
            Div(attrs = {
                style { fontSize(15.px); fontWeight(600); color(Gray900); property("overflow-wrap", "anywhere") }
            }) { Text(item.title) }
            val sub = listOf(item.author, item.year.orEmpty()).filter { it.isNotBlank() }.joinToString(" · ")
            if (sub.isNotEmpty()) {
                Div(attrs = { style { fontSize(13.px); color(Gray600); marginTop(2.px) } }) { Text(sub) }
            }
        }

        Div(attrs = {
            style {
                property("flex-shrink", "0")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.FlexEnd)
                property("max-width", "140px")
            }
        }) {
            when (item.status) {
                BulkResultStatus.QUEUED -> Div(attrs = { style { fontSize(13.px); color(Gray500) } }) { Text("Waiting") }
                BulkResultStatus.RUNNING -> Div(attrs = {
                    style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); property("gap", "6px"); fontSize(13.px); color(Gray600) }
                }) {
                    Span(attrs = {
                        style {
                            width(14.px); height(14.px)
                            borderRadius(50.percent)
                            property("border", "2px solid ${"#D4D4D4"}")
                            property("border-top-color", "#0E7C7B")
                            property("box-sizing", "border-box")
                            property("animation", "spin 0.8s linear infinite")
                        }
                    })
                    Text("Analyzing…")
                }
                BulkResultStatus.DONE -> Div(attrs = {
                    style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); property("gap", "6px") }
                }) {
                    val (label, bg, fg) = when (item.verdict) {
                        BulkVerdict.MINOR -> Triple("Minor", ScoreGreenBg, PositiveText)
                        BulkVerdict.MODERATE -> Triple("Moderate", ScoreAmberBg, HadithTextDark)
                        BulkVerdict.MAJOR -> Triple("Major", ScoreRedBg, ScoreRed)
                        null -> Triple("Done", Gray100, Gray600)
                    }
                    Span(attrs = {
                        style {
                            padding(3.px, 10.px)
                            borderRadius(10.px)
                            fontSize(12.px)
                            fontWeight(700)
                            backgroundColor(bg)
                            color(fg)
                        }
                    }) { Text(label) }
                    Span(attrs = { style { fontSize(18.px); color(Gray400); property("line-height", "1") } }) { Text("›") }
                }
                BulkResultStatus.FAILED -> {
                    Div(attrs = { style { fontSize(13.px); color(Gray500); property("text-align", "right") } }) { Text("Couldn't analyze") }
                    Div(attrs = { style { fontSize(11.px); color(Gray500); marginTop(2.px); property("text-align", "right") } }) {
                        Text("You weren't charged for this one")
                    }
                }
                BulkResultStatus.REFUSED -> {
                    Div(attrs = { style { fontSize(13.px); color(Gray500); property("text-align", "right") } }) { Text("Not analyzed") }
                    val why = when (item.error) {
                        "out_of_searches" -> "No searches left in your pack"
                        "usage_cap" -> "Weekly limit reached"
                        else -> "Couldn't start"
                    }
                    Div(attrs = { style { fontSize(11.px); color(Gray500); marginTop(2.px); property("text-align", "right") } }) { Text(why) }
                }
            }
        }
    }
}

// ── Edit / add dialog ───────────────────────────────────────────────────────────────

@Composable
private fun BulkEditDialog(
    heading: String,
    initialTitle: String,
    initialAuthor: String,
    onSave: (title: String, author: String) -> Unit,
    onDismiss: () -> Unit,
) {
    var title by remember { mutableStateOf(initialTitle) }
    var author by remember { mutableStateOf(initialAuthor) }
    val canSave = title.isNotBlank()
    val fieldStyle: StyleScope.() -> Unit = {
        width(100.percent)
        padding(10.px, 12.px)
        fontSize(15.px)
        borderRadius(10.px)
        property("border", "1.5px solid #D4D4D4")
        property("box-sizing", "border-box")
        property("font-family", "inherit")
    }

    // Scrim — same shape as ImageCandidatePicker's.
    Div(attrs = {
        onClick { onDismiss() }
        style {
            property("position", "fixed")
            property("top", "0"); property("left", "0"); property("right", "0"); property("bottom", "0")
            property("background", "rgba(15, 23, 23, 0.5)")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "1000")
            padding(20.px)
        }
    }) {
        Div(attrs = {
            onClick { it.stopPropagation() }
            style {
                backgroundColor(Color.white)
                borderRadius(20.px)
                property("max-width", "380px")
                width(100.percent)
                padding(22.px)
                property("box-sizing", "border-box")
                property("box-shadow", "0 24px 70px rgba(14, 124, 123, 0.28), 0 4px 12px rgba(0,0,0,0.08)")
            }
        }) {
            Div(attrs = {
                style { fontSize(17.px); fontWeight(700); color(Gray900); marginBottom(14.px) }
            }) { Text(heading) }

            Div(attrs = { style { fontSize(12.px); fontWeight(600); color(Gray600); marginBottom(4.px) } }) { Text("Title") }
            TextInput(value = title) {
                onInput { title = it.value }
                onKeyDown { if (it.key == "Enter" && canSave) onSave(title, author) }
                style(fieldStyle)
            }
            Div(attrs = { style { fontSize(12.px); fontWeight(600); color(Gray600); marginTop(12.px); marginBottom(4.px) } }) { Text("Author") }
            TextInput(value = author) {
                onInput { author = it.value }
                onKeyDown { if (it.key == "Enter" && canSave) onSave(title, author) }
                style(fieldStyle)
            }

            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                    property("gap", "8px")
                    marginTop(18.px)
                }
            }) {
                BulkTextLink("Cancel", onDismiss)
                BulkButton("Save", primary = true, enabled = canSave) { onSave(title, author) }
            }
        }
    }
}

// ── Main-screen banner ──────────────────────────────────────────────────────────────

/**
 * Compact card on the main search view that leads back to a running or finished bulk batch while the
 * scan screens are closed. Mirrors the mobile banner in composeApp.
 */
@Composable
fun BulkBatchBanner(batch: BulkBatchState, onOpen: () -> Unit, onDismiss: () -> Unit) {
    InjectSpinKeyframes()
    val total = batch.total
    val noun = if (total == 1) "book" else "books"
    val pct = if (total == 0) 0 else (batch.finished * 100 / total)
    Div(attrs = {
        attr("role", "button")
        attr("tabindex", "0")
        onClick { onOpen() }
        onKeyDown { if (it.key == "Enter") onOpen() }
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            property("gap", "12px")
            property("box-sizing", "border-box")
            width(100.percent)
            marginTop(12.px)
            padding(12.px, 14.px)
            borderRadius(RadiusXLarge.unsafeCast<CSSNumeric>())
            property("background", "#FFFFFF")
            property("border", "1px solid #E5E5E5")
            property("box-shadow", NeuBoxShadow)
            cursor("pointer")
        }
    }) {
        if (batch.done) {
            Div(attrs = {
                style {
                    width(24.px); height(24.px)
                    property("flex-shrink", "0")
                    borderRadius(50.percent)
                    backgroundColor(PrimaryPale)
                    color(Primary)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    fontSize(14.px)
                    fontWeight(700)
                }
            }) { Text("✓") }
        } else {
            Span(attrs = {
                style {
                    width(20.px); height(20.px)
                    property("flex-shrink", "0")
                    borderRadius(50.percent)
                    property("border", "2px solid #D4D4D4")
                    property("border-top-color", "#0E7C7B")
                    property("box-sizing", "border-box")
                    property("animation", "spin 0.8s linear infinite")
                }
            })
        }
        Div(attrs = { style { property("flex", "1"); property("min-width", "0") } }) {
            Div(attrs = { style { fontSize(15.px); fontWeight(700); color(Gray900) } }) {
                Text(if (batch.done) "Your $total $noun ${if (total == 1) "is" else "are"} ready" else "Analyzing $total $noun")
            }
            Div(attrs = { style { fontSize(13.px); color(Gray600); marginTop(2.px) } }) {
                Text(if (batch.done) "View the results" else "${batch.finished} of $total finished")
            }
            if (batch.offline) {
                Div(attrs = { style { fontSize(12.px); color(Gray500); marginTop(2.px) } }) {
                    Text("Can't reach the server. Still checking.")
                }
            }
            if (!batch.done) {
                Div(attrs = {
                    style {
                        height(4.px)
                        marginTop(8.px)
                        borderRadius(2.px)
                        backgroundColor(Gray200)
                        property("overflow", "hidden")
                    }
                }) {
                    Div(attrs = {
                        style {
                            height(100.percent)
                            width(pct.percent)
                            backgroundColor(Primary)
                            property("transition", "width 0.4s ease")
                        }
                    })
                }
            }
        }
        if (batch.done) {
            Button(attrs = {
                attr("aria-label", "Dismiss")
                onClick { it.stopPropagation(); onDismiss() }
                style {
                    width(32.px); height(32.px)
                    property("flex-shrink", "0")
                    property("background", "transparent")
                    property("border", "none")
                    borderRadius(50.percent)
                    fontSize(14.px)
                    color(Gray400)
                    cursor("pointer")
                }
            }) { Text("✕") }
        }
    }
}
